import _get from 'lodash/get';
import {
  WidgetsKindEnum,
  WidgetTemplatesKindEnum,
  Widget,
  WidgetsField,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';

/**
 * Maps WidgetTemplatesKindEnum to WidgetsKindEnum
 * Both enums have the same values for Announcement and ExternalLink
 */
function mapTemplateKindToWidgetKind(
  templateKind: WidgetTemplatesKindEnum,
): WidgetsKindEnum {
  const kindMap: Record<WidgetTemplatesKindEnum, WidgetsKindEnum> = {
    [WidgetTemplatesKindEnum.Announcement]: WidgetsKindEnum.Announcement,
    [WidgetTemplatesKindEnum.ExternalLink]: WidgetsKindEnum.ExternalLink,
    [WidgetTemplatesKindEnum.ActiveInterventions]:
      WidgetsKindEnum.ActiveInterventions,
    [WidgetTemplatesKindEnum.Clock]: WidgetsKindEnum.Clock,
    [WidgetTemplatesKindEnum.QuickCreation]: WidgetsKindEnum.QuickCreation,
    [WidgetTemplatesKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed,
    [WidgetTemplatesKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed,
    [WidgetTemplatesKindEnum.SitesMap]: WidgetsKindEnum.SitesMap,
    [WidgetTemplatesKindEnum.WeatherToday]: WidgetsKindEnum.WeatherToday,
    [WidgetTemplatesKindEnum.WeatherWeek]: WidgetsKindEnum.WeatherWeek,
  };

  return kindMap[templateKind] || (templateKind as unknown as WidgetsKindEnum);
}

/**
 * Prepares widget data from widget template form values for preview.
 * Converts form values structure to Widget-like object for WidgetContent.
 */
export default function prepareWidgetTemplatePreview(
  values: Values,
): Partial<Widget> | null {
  // Get kind from form values (RadioButtonGroup returns { value, label })
  const kindOption = _get(values, 'kind') as SelectOption | undefined;
  const templateKind = kindOption?.value as WidgetTemplatesKindEnum | undefined;

  if (!templateKind) {
    return null;
  }

  const kind = mapTemplateKindToWidgetKind(templateKind);

  // Common fields
  const title = _get(values, 'title', '') as string;
  const description = _get(values, 'description', '') as string;

  // Build the widget object
  const fields: Partial<WidgetsField> = {};

  const widget: Partial<Widget> = {
    id: 'preview',
    kind,
    title: title || null,
    description: description || null,
    fields: fields as WidgetsField,
  };

  // Widget-specific fields based on kind
  switch (templateKind) {
    case WidgetTemplatesKindEnum.Announcement: {
      // Background color
      const backgroundColorOption = _get(values, 'backgroundColor') as
        | SelectOption
        | undefined;
      if (backgroundColorOption?.value) {
        fields.backgroundColor = backgroundColorOption.value as string;
      }

      // Content (rich text) - maps to widget.description for AnnouncementWidget
      const content = _get(values, 'content', '') as string;
      if (content) {
        widget.description = content;
      }

      // Image attachment
      const imageValue = _get(values, 'image');
      if (imageValue) {
        widget.attachmentsAttached = [
          imageValue as Widget['attachmentsAttached'][0],
        ];
      }
      break;
    }

    case WidgetTemplatesKindEnum.ExternalLink: {
      // Link URL
      const link = _get(values, 'link', '') as string;
      if (link) {
        fields.link = link;
      }

      // Icon image attachment (for ExternalLink)
      const iconImageValue = _get(values, 'iconImage');
      if (iconImageValue) {
        widget.attachmentsAttached = [
          iconImageValue as Widget['attachmentsAttached'][0],
        ];
      }
      break;
    }

    default:
      break;
  }

  return widget;
}
